import { installSkyCinderFrost, updateSkyCinderFrost } from './236-sky-cinder-frost.mjs';
import { createSkyHourglassEchoes } from './231-sky-hourglass-echoes.mjs';
import { createSkyOrchidWaterMaterial } from './230-sky-orchid-effects.mjs';
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';
import {
  compileSkyAuthoredRig,
  sampleSkyAuthoredPose,
} from '../../../packages/domain/src/115-sky-authored-pose.mjs';

/** Exact canonical340 exports. The mobile mesh remains independently compiled
 * for contact: reducing triangles must never retain the full mesh's collision. */
export const SKY_AUTHORED_BOSSES = Object.freeze({
  'twin-executioners': Object.freeze({
    file: '11-executioner-ivory',
    hashes: [
      '2c60804f0e490ce25947d7e117028af4b8e0389d20cd1d761148b46bf6a51188',
      'a929ea72d77b747abea63d39b5e82ab05b64229ea6732fa9e65a69584e2242b5',
    ],
    idle: 'Idle',
  }),
  'scrap-emperor': Object.freeze({
    file: '09-scrap-emperor',
    hashes: [
      'fe446370f3befebe1da066adbeac8e794eeeaa280a7176a7ce416b30136003f7',
      '4f46a0511c8fe69ecf6dca896806063925a666329f013c72c830be626ed04f14',
    ],
    idle: 'Idle',
  }),
  vesper: Object.freeze({
    file: '10-vesper',
    hashes: [
      '50510995b4c4afdc073f9da63629a317f23a427cf4a08250ea68a8710baabfdc',
      '8bc48f7b482cc80b56c12a5e4a9298937553bd02231a79623837f1985bcb55f6',
    ],
    idle: 'Idle',
  }),
  'hourglass-king': Object.freeze({
    file: '08-hourglass-king',
    hashes: [
      'f7edc58b1854ccde9baba850fa1508f92955d5161054a32ce7de3b066ef323bf',
      'e37e106ddb24f54b2ca4932ef569f73cb1d54a7f7dab78b5e585b8ef40f54cd0',
    ],
    idle: 'Idle',
  }),
  'prism-widow': Object.freeze({
    file: '04-prism-widow',
    hashes: [
      '7008fc0adf0ea90268e491b585474bb1a444a25c6ac571fd18c3641de772bf57',
      'edbb66b9cdfb66463018eb815f908c341c96ed2f825f5d96df5521dd55c194fe',
    ],
    idle: 'Idle',
  }),
  cinderheart: Object.freeze({
    file: '05-cinderheart',
    hashes: [
      '1db5128ccf64056eab8e6a6272979090afcc309497b9da994f29cf9a6d7b73a8',
      'e5c191fe5376aa4d85660c6e0669b4a2828a19721fd244e5e91f29ec471d68bb',
    ],
    idle: 'Idle',
  }),
  'mourning-bride': Object.freeze({
    file: '06-mourning-bride',
    hashes: [
      'd19b6a72bdefa609eebd6de40da4e953b1ee3f6332900893e2b9e8cf1b172c43',
      '53ce231efeeddcd69a2167afe760c432198938434aef576062fce103d5487194',
    ],
    idle: 'Glide',
  }),
  'iron-orchid': Object.freeze({
    file: '07-iron-orchid',
    hashes: [
      '7b0b142d69dd7fc5dca6304dbc134d4f05edbd40f339f9d185e51dfaf25b5b75',
      '97e6ca81eca392300a5a527a04ec45329ac9b42891f99bfb82e8fba02138654e',
    ],
    idle: 'Idle',
  }),
});

/** Interactive source props use the same skin/pose contract as guardians. Their
 * canonical files are already present in the imported environment inventory. */
export const SKY_AUTHORED_PROPS = Object.freeze({
  'twin-gantry': Object.freeze({
    file: 'imported-worlds/11-twin-gantry',
    hashes: [
      'f2c15824681be4d39644543e78757a6518f45eae8546ddfacc154686bf847498',
      '7d616e22f1881ba6ec90686a2b9716413da274827b0891646840a5f058864415',
    ],
    idle: 'Idle',
  }),
  'executioner-crimson': Object.freeze({
    file: 'authored-bosses/11-executioner-crimson',
    hashes: [
      '1e18d6ef3fb9ca2a860ea5ae83c1ffc53c36360de4a6dfde3d770711ebb94a50',
      '2dcf39a40905c35feeea76a1ff2d87873c09295f3677b0fc9957c8a6324c50d2',
    ],
    idle: 'Idle',
  }),
  'scrap-supplies': Object.freeze({
    file: 'imported-worlds/09-scrap-supplies',
    hashes: [
      '1cc6c470452ee88b1c2f23e1927f7757d16faf7e28032dc850b56c3a6ef50b55',
      '70f71b519252c5c79aa8428e5bf7716764096cc1bfa6f8df775543af4a90c32a',
    ],
    idle: 'Idle',
  }),
  'vesper-crystal': Object.freeze({
    file: 'imported-worlds/10-vesper-crystal',
    hashes: [
      '61f4da11962209557c2cdade19142d0db673f80a752b7f00fed91565ca452326',
      '76d112999908807a349dfb816f58b2eca48b25505c2e74e37cd4f034f926f2d6',
    ],
    idle: 'Idle',
  }),
  'mourning-bride-wreck': Object.freeze({
    file: 'imported-worlds/06-wreck',
    hashes: [
      '959b50894c8d85493ed6a0310b3ffda3b2db317b5603d7b46a43572f27b32f00',
      '0232823175135763f634d96600edfad8722b49c6db53d10df23f297883b14411',
    ],
    idle: 'Idle',
  }),
  'mourning-bride-echo': Object.freeze({
    file: 'authored-bosses/06-phantom-echo',
    hashes: [
      'bd1d5063ef3b8bad68efbeda7a4f7dd8b6940582276218869a8b29fa106da9de',
      '1a0c3c82d0fc821abcbb1d53a493fde20866dcf70bd24658b93848c86a819cca',
    ],
    idle: 'Flight',
  }),
  'iron-orchid-irrigation': Object.freeze({
    file: 'imported-worlds/07-platform',
    hashes: [
      '102e2005c9c776ab85f2cc57a860fe3734e3710ba8c7c0354f08eaa2c2e01736',
      'a674e698e4b49cff71493727a505a1a2ee1b4d364cf807757bde7b4719233067',
    ],
    idle: 'Idle',
  }),
  'prism-mirrors': Object.freeze({
    file: 'imported-worlds/04-orbital',
    hashes: [
      '5c6667e76654ae46ef1eb37aef0cb55772d22b5df1bfbce87a5288c7f0f687c7',
      '085409dbc6a7c0b0ad08f9bcf974f213edfc0ff2b1197d35abc0c6da2fa25e22',
    ],
    idle: 'Idle',
  }),
  'cinderheart-slag': Object.freeze({
    file: '25-rock',
    mobile: false,
    hashes: [
      'dc55729a38317494f38307a44dbe1e49eebcb7555b9e444c0195a206a496378a',
      'dc55729a38317494f38307a44dbe1e49eebcb7555b9e444c0195a206a496378a',
    ],
    idle: null,
  }),
  'cinderheart-coolant': Object.freeze({
    file: 'imported-worlds/05-coolant',
    hashes: [
      '7bca8c6528217a045e8f1bce4796e9675bf5a6d3c81defd6c7afc6c6a6f3c136',
      'd46871faf14fdebc89068d0801b13cfe79ecd5832bac428ecdb08896b6f21a2d',
    ],
    idle: 'Idle',
  }),
});

/** Exact authored wave-craft exports, pinned to canonical340 without rebaking. */
export const SKY_AUTHORED_ENEMIES = Object.freeze(
  Object.fromEntries(
    Object.entries({
      'enemy-gargoyle': {
        file: 'authored-bosses/03-gargoyle',
        hashes: [
          '6d451a53a5f2adec883f46c68bbc91565e5c33f47121598ee8493fbd45d3eb33',
          '27136fe4ea21ef29ff40a3e39ef5a151fba4dc11d15d1c29c527b6798a078342',
        ],
        idle: 'Flap',
      },
      'enemy-mirror-scarab': {
        file: 'authored-bosses/04-mirror-scarab',
        hashes: [
          '1de7a08912cc4d07609cff9058aa6d0d33946a1dcafbb8974561a572f4526c51',
          '5d9427773155a3c6fa32964d27fcf8d3fea4f3932b0ec815bd8739d715f2fccc',
        ],
        idle: 'Flight',
      },
      'enemy-slag-skimmer': {
        file: 'authored-bosses/05-slag-skimmer',
        hashes: [
          '2dc6f13fb5b8aacc9d42667005050764555df0394037e25895eb9b184f969381',
          '1262fbeefaa0a5815895e6decd9053e13960c835809ca5de6fca340afb37d143',
        ],
        idle: 'Flight',
      },
      'enemy-phantom-echo': {
        file: 'authored-bosses/06-phantom-echo',
        hashes: [
          'bd1d5063ef3b8bad68efbeda7a4f7dd8b6940582276218869a8b29fa106da9de',
          '1a0c3c82d0fc821abcbb1d53a493fde20866dcf70bd24658b93848c86a819cca',
        ],
        idle: 'Flight',
      },
      'enemy-homing-seed': {
        file: 'authored-bosses/07-homing-seed',
        hashes: [
          '4fde4abc8fb05498800ded32d2e4803a99f0e60c8f1d11b62def6fcde6608a29',
          'eab03282be38a3349d8c5ac6406e6e07a6c8d886b80b9aa4c7dae13fabab028b',
        ],
        idle: 'Flight',
      },
    }).map(([id, spec]) => [id, Object.freeze(spec)]),
  ),
);
export function skyAuthoredEnemySpec(id) {
  return Object.hasOwn(SKY_AUTHORED_ENEMIES, id) ? SKY_AUTHORED_ENEMIES[id] : null;
}

export function skyAuthoredPropSpec(id) {
  return Object.hasOwn(SKY_AUTHORED_PROPS, id) ? SKY_AUTHORED_PROPS[id] : null;
}
export function skyAuthoredAssetSpec(id) {
  return skyAuthoredBossSpec(id) ?? skyAuthoredPropSpec(id) ?? skyAuthoredEnemySpec(id);
}

export function skyAuthoredBossSpec(worldId) {
  return Object.hasOwn(SKY_AUTHORED_BOSSES, worldId) ? SKY_AUTHORED_BOSSES[worldId] : null;
}

/** Readiness pins the exact source export before parsing. A stale CDN or wrong
 * tier fails explicitly instead of silently changing the visible contact hull. */
export async function verifySkyAuthoredBossAsset(bytes, worldId, mobile = false) {
  const spec = skyAuthoredAssetSpec(worldId);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  const hash = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
  if (!spec || hash !== spec.hashes[mobile ? 1 : 0])
    throw new Error('Authored guardian artwork does not match its physical assembly.');
}

function values(attribute, width) {
  const result = [];
  for (let i = 0; i < attribute.count; i++)
    for (let component = 0; component < width; component++)
      result.push(attribute.getComponent(i, component));
  return result;
}

/** Extract decoded GLTF data into the pure domain's numeric rig contract. Node
 * indices are preorder and stable across SkeletonUtils clones; hierarchy, bind
 * transforms, skin weights and animation channels retain source metres exactly.
 * Unsupported morph/cubic tracks fail readiness rather than diverge in combat. */
export function extractSkyAuthoredBossRig(T, gltf, worldId) {
  const scene = gltf.scene,
    objects = [],
    indices = new Map();
  scene.updateMatrixWorld(true);
  scene.traverse((node) => {
    indices.set(node, objects.length);
    objects.push(node);
  });
  const nodes = objects.map((node, id) => ({
    id,
    name: node.name,
    parent: indices.get(node.parent) ?? -1,
    position: node.position.toArray(),
    quaternion: node.quaternion.toArray(),
    scale: node.scale.toArray(),
  }));
  const meshes = [];
  for (const node of objects) {
    if (!node.isMesh) continue;
    const geometry = node.geometry;
    if (geometry.morphAttributes.position?.length)
      throw new Error('Authored guardian morph surfaces require a shared contact adapter.');
    const position = geometry.getAttribute('position');
    const skin = node.isSkinnedMesh;
    const mesh = {
      node: indices.get(node),
      name: node.name,
      positions: values(position, 3),
      indices: geometry.index
        ? values(geometry.index, 1)
        : Array.from({ length: position.count }, (_, i) => i),
      ...(skin
        ? {
            joints: values(geometry.getAttribute('skinIndex'), 4),
            weights: values(geometry.getAttribute('skinWeight'), 4),
            inverseBindMatrices: node.skeleton.boneInverses.map((matrix) => matrix.toArray()),
            jointNodes: node.skeleton.bones.map((bone) => indices.get(bone)),
            bindMatrix: node.bindMatrix.toArray(),
            bindMatrixInverse: node.bindMatrixInverse.toArray(),
            bindMode: node.bindMode,
          }
        : {}),
    };
    meshes.push(mesh);
  }
  const clips = gltf.animations.map((clip) => ({
    name: clip.name,
    duration: clip.duration,
    tracks: clip.tracks.map((track) => {
      const binding = T.PropertyBinding.parseTrackName(track.name);
      const target = T.PropertyBinding.findNode(scene, binding.nodeName);
      const interpolation = track.getInterpolation();
      if (
        !indices.has(target) ||
        !['position', 'quaternion', 'scale'].includes(binding.propertyName) ||
        ![T.InterpolateLinear, T.InterpolateDiscrete].includes(interpolation) ||
        track.createInterpolant.isInterpolantFactoryMethodGLTFCubicSpline
      )
        throw new Error('Authored guardian has an unsupported animation channel.');
      return {
        node: indices.get(target),
        property: binding.propertyName,
        times: Array.from(track.times),
        values: Array.from(track.values),
        interpolation: interpolation === T.InterpolateDiscrete ? 'STEP' : 'LINEAR',
      };
    }),
  }));
  const rig = compileSkyAuthoredRig({
    worldId,
    nodes,
    meshes,
    clips,
    sockets: objects
      .filter((node) => node.userData.socket)
      .map((node) => ({ name: node.name, node: indices.get(node) })),
  });
  if (!rig) throw new Error('Authored guardian failed physical rig validation.');
  return rig;
}

/** Clones retain original geometry/materials, own only their skeletons, and
 * display exactly the domain pose. No independent mixer or visual bank can move
 * a socket away from its hittable surface. Loading/warmup uses the same sampler. */
export function createSkyAuthoredBossArt(T, gltf, rig) {
  const live = new Set();
  const echoes = rig.worldId === 'hourglass-king' ? createSkyHourglassEchoes(T, gltf) : null;
  const rest = sampleSkyAuthoredPose(rig, skyAuthoredAssetSpec(rig.worldId).idle, 0);
  function apply(group, pose) {
    const nodes = group.userData.authoredNodes;
    if (pose.nodes.length !== nodes.length)
      throw new Error('Authored guardian pose does not match its rig.');
    for (let index = 0; index < nodes.length; index++) {
      const node = nodes[index],
        transform = pose.nodes[index];
      node.position.fromArray(transform.position);
      node.quaternion.fromArray(transform.quaternion);
      node.scale.fromArray(transform.scale);
    }
    group.updateMatrixWorld(true);
  }
  function create() {
    const group = new T.Group(),
      scene = cloneSkeleton(gltf.scene),
      nodes = [],
      skeletons = new Set(),
      materials = new Set();
    scene.traverse((node) => {
      nodes.push(node);
      if (rig.worldId === 'iron-orchid-irrigation' && node.name === 'Waterfall_route_curtain') {
        node.material = createSkyOrchidWaterMaterial(T, node.geometry);
        materials.add(node.material);
        node.castShadow = false;
        node.receiveShadow = false;
        node.renderOrder = 3;
      }
      // Hot slag owns its material instances: cooling one rock cannot tint the
      // cached source mesh or another persistent obstacle. Geometry stays shared.
      if (
        ['cinderheart-slag', 'prism-widow', 'mourning-bride'].includes(rig.worldId) &&
        node.isMesh
      ) {
        const clone = (material) => {
          const owned = material.clone();
          owned.userData.slagBaseEmissive = owned.emissive?.clone();
          owned.userData.slagBaseIntensity = owned.emissiveIntensity;
          materials.add(owned);
          return owned;
        };
        node.material = Array.isArray(node.material)
          ? node.material.map(clone)
          : clone(node.material);
      }
      if (rig.worldId === 'cinderheart') installSkyCinderFrost(T, node, materials);
      if (node.isSkinnedMesh) {
        skeletons.add(node.skeleton);
        node.frustumCulled = false;
      }
    });
    group.add(scene);
    group.userData.skyAuthoredArt = rig.worldId;
    group.userData.authoredNodes = nodes;
    group.userData.authoredSkeletons = skeletons;
    group.userData.authoredMaterials = materials;
    live.add(group);
    apply(group, rest);
    return group;
  }
  function animate(group, item, reduced = false) {
    if ((item.authoredRig || item.authoredEnemyRig) && !item.authoredPose)
      throw new Error('Authored guardian has no physical pose.');
    apply(group, item.authoredPose ?? rest);
    if (rig.worldId === 'cinderheart') updateSkyCinderFrost(group, item);
    const heat = Math.max(0, Math.min(1, item.slagHeat ?? 0));
    for (const material of group.userData.authoredMaterials) {
      if (material.uniforms?.waterTime)
        material.uniforms.waterTime.value = reduced ? 0 : Math.max(0, item.age ?? 0);
      if (rig.worldId !== 'cinderheart-slag' || !material.emissive) continue;
      material.emissive.copy(material.userData.slagBaseEmissive).lerp(new T.Color(0xff4a09), heat);
      material.emissiveIntensity = material.userData.slagBaseIntensity + heat * 2.4;
    }
    if (rig.worldId === 'mourning-bride' || rig.worldId === 'prism-widow') {
      group.userData.authoredNodes.forEach((node, index) => {
        if (!node.isMesh) return;
        const state = item.brideMaterialStates?.[index];
        const opacity = state?.opacity ?? (item.prismIllusion ? 0.22 : 1);
        node.castShadow = !item.prismIllusion && opacity > 0.8;
        node.receiveShadow = !item.prismIllusion;
        for (const material of Array.isArray(node.material) ? node.material : [node.material]) {
          const transparent = opacity < 1;
          if (material.transparent !== transparent) {
            material.transparent = transparent;
            material.needsUpdate = true;
          }
          material.opacity = opacity;
          material.depthWrite = !transparent;
          material.envMapIntensity = item.prismIllusion ? 0 : 1.4;
          if (material.emissive) {
            material.emissive.copy(material.userData.slagBaseEmissive);
            material.emissiveIntensity = material.userData.slagBaseIntensity;
            if (state?.lit) {
              material.emissive.set('#87dfff');
              material.emissiveIntensity = 0.65;
            }
          }
        }
      });
    }
    if (item.rootTransform) {
      group.position.fromArray(item.rootTransform.position);
      group.quaternion.fromArray(item.rootTransform.quaternion);
      group.scale.fromArray(item.rootTransform.scale);
      group.updateMatrixWorld(true);
    }
    echoes?.update(group, item, reduced);
  }
  function release(group) {
    echoes?.release(group);
    if (!live.delete(group)) return;
    for (const skeleton of group.userData.authoredSkeletons) skeleton.dispose();
    group.userData.authoredSkeletons.clear();
    for (const material of group.userData.authoredMaterials) material.dispose();
    group.userData.authoredMaterials.clear();
    group.userData.authoredNodes.length = 0;
  }
  return {
    create,
    animate,
    release,
    dispose() {
      for (const group of live) release(group);
      echoes?.dispose();
    },
  };
}
